import { useEffect, useState, useCallback } from "react";
import { Download, Share, Plus, X, Smartphone, MonitorDown, Check, Share2, Copy, MessageCircle, Mail, Send, Twitter, Facebook } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

/* Shared PWA install state.
   Exposes:
   - canInstall: Chrome/Edge/Android fired `beforeinstallprompt` and we captured it
   - isIOS:      iOS Safari (needs manual Add-to-Home-Screen)
   - standalone: already running as an installed PWA (display-mode: standalone)
   - install():  trigger the native prompt
   - visible:    whether to render the install button
   Policy: show the button whenever the app is NOT already installed. If no native
   prompt is available we open a modal with per-platform instructions. */
export const usePWAInstall = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [standalone, setStandalone] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);

  useEffect(() => {
    const ua = window.navigator.userAgent || "";
    // iOS detection — covers iPhone/iPad/iPod and iPadOS 13+ (which reports as Mac + touch)
    const iOS =
      /iPad|iPhone|iPod/.test(ua) ||
      (navigator.platform === "MacIntel" && (navigator.maxTouchPoints || 0) > 1);
    setIsIOS(iOS);
    setIsAndroid(/Android/i.test(ua));

    // Only trust `display-mode: standalone` when NOT inside an iframe.
    // Many WebView / preview environments (e.g. Emergent Preview) report
    // standalone=true falsely because they have no browser chrome.
    // The real installed PWA opens in its own top-level window.
    const inIframe = (() => {
      try { return window.self !== window.top; } catch (_) { return true; }
    })();
    const inStandalone =
      !inIframe && window.matchMedia("(display-mode: standalone)").matches;
    setStandalone(inStandalone);

    const onBefore = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setDeferredPrompt(null);
      setStandalone(true);
    };
    window.addEventListener("beforeinstallprompt", onBefore);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = useCallback(async () => {
    if (!deferredPrompt) return false;
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice.catch(() => ({ outcome: "dismissed" }));
    if (choice.outcome === "accepted") setDeferredPrompt(null);
    return choice.outcome === "accepted";
  }, [deferredPrompt]);

  return {
    canInstall: !!deferredPrompt,
    isIOS,
    isAndroid,
    standalone,
    install,
    visible: true, // always show — hidden state handled by consumers if needed
  };
};

/* Platform-aware install instructions modal.
   iOS → Safari Share → Add to Home Screen
   Android (no native prompt) → Chrome menu → Install app / Add to Home Screen
   Desktop/other → Install from the browser menu or address bar install icon */
export const InstallHelp = ({ open, onOpenChange, lang = "ar", theme = "dark", platform = "generic" }) => {
  const dark = theme === "dark";
  const ar = lang === "ar";

  const steps =
    platform === "ios"
      ? [
          {
            icon: <Share className="h-3.5 w-3.5" />,
            label: ar ? "مشاركة" : "Share",
            text: ar ? "اضغط زرّ المشاركة في شريط سفاري." : "Tap the Share button in Safari's toolbar.",
          },
          {
            icon: <Plus className="h-3.5 w-3.5" />,
            label: ar ? "إضافة إلى الشاشة الرئيسية" : "Add to Home Screen",
            text: ar ? "اختر «إضافة إلى الشاشة الرئيسية» ثم اضغط «إضافة»." : "Choose “Add to Home Screen” then tap “Add”.",
          },
        ]
      : platform === "android"
      ? [
          {
            icon: <MonitorDown className="h-3.5 w-3.5" />,
            label: ar ? "القائمة ⋮" : "Menu ⋮",
            text: ar ? "افتح قائمة المتصفح (ثلاث نقاط) من الأعلى." : "Open the browser menu (three dots) at the top.",
          },
          {
            icon: <Plus className="h-3.5 w-3.5" />,
            label: ar ? "تثبيت التطبيق" : "Install app",
            text: ar
              ? "اختر «تثبيت التطبيق» أو «إضافة إلى الشاشة الرئيسية»."
              : "Choose “Install app” or “Add to Home Screen”.",
          },
        ]
      : [
          {
            icon: <MonitorDown className="h-3.5 w-3.5" />,
            label: ar ? "تثبيت" : "Install",
            text: ar
              ? "ابحث عن أيقونة التثبيت في شريط العنوان، أو من قائمة المتصفح اختر «تثبيت برولانسر»."
              : "Look for the install icon in the address bar, or open the browser menu and choose “Install Prolancer”.",
          },
          {
            icon: <Plus className="h-3.5 w-3.5" />,
            label: ar ? "تأكيد" : "Confirm",
            text: ar ? "اضغط «تثبيت» لإضافته كتطبيق مستقل." : "Click “Install” to add it as a standalone app.",
          },
        ];

  const title =
    platform === "ios"
      ? ar ? "ثبّت برولانسر على الآيفون" : "Install Prolancer on iPhone"
      : platform === "android"
      ? ar ? "ثبّت برولانسر على أندرويد" : "Install Prolancer on Android"
      : ar ? "ثبّت تطبيق برولانسر" : "Install Prolancer app";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="pwa-install-help"
        dir={ar ? "rtl" : "ltr"}
        className={`${dark ? "bg-[#182138] text-white border-[#18c98f]/40" : "bg-white text-[#182138] border-[#18c98f]/50"} max-w-md`}
      >
        <DialogHeader>
          <DialogTitle className={`flex items-center gap-2 ${ar ? "font-arabic" : "font-outfit"}`}>
            <Smartphone className="h-5 w-5 text-[#18c98f]" />
            {title}
          </DialogTitle>
          <DialogDescription className={`${dark ? "text-white/70" : "text-[#2d2c35]/80"} ${ar ? "font-arabic" : "font-outfit"}`}>
            {ar
              ? "خطوتان فقط لإضافة الحاسبة لشاشتك كتطبيق مستقل:"
              : "Two quick steps to add the calculator as a standalone app:"}
          </DialogDescription>
        </DialogHeader>

        <ol className={`space-y-3 mt-2 ${ar ? "font-arabic" : "font-outfit"}`}>
          {steps.map((s, i) => (
            <li
              key={i}
              className={`flex items-start gap-3 p-3 rounded-xl border ${dark ? "border-[#2e3a5c] bg-[#1f2a47]" : "border-[#e5e5ea] bg-[#f3f8ff]"}`}
            >
              <span className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-[#18c98f] text-white font-black text-sm shrink-0">
                {i + 1}
              </span>
              <div className="flex-1 text-sm leading-relaxed">
                <span className="inline-flex items-center gap-1 me-1 px-2 py-0.5 rounded-md bg-[#18c98f]/15 text-[#18c98f] font-bold">
                  {s.icon}
                  {s.label}
                </span>
                {s.text}
              </div>
            </li>
          ))}
        </ol>

        <p className={`mt-3 text-xs ${dark ? "text-white/60" : "text-[#2d2c35]/70"} ${ar ? "font-arabic" : "font-outfit"}`}>
          {ar
            ? "ستظهر أيقونة برولانسر الخضراء على شاشتك، وتفتح الحاسبة كتطبيق مستقل بدون شريط عنوان."
            : "The green Prolancer icon will appear on your screen and open as a standalone app without a URL bar."}
        </p>

        <button
          onClick={() => onOpenChange(false)}
          data-testid="pwa-install-help-close"
          className="mt-2 inline-flex items-center justify-center gap-2 w-full h-10 rounded-full bg-[#18c98f] text-white font-extrabold hover:brightness-110"
        >
          <X className="h-4 w-4" />
          {ar ? "فهمت" : "Got it"}
        </button>
      </DialogContent>
    </Dialog>
  );
};

// Backward-compat alias (older imports used `IOSInstallHelp`).
export const IOSInstallHelp = (props) => <InstallHelp platform="ios" {...props} />;

/* Shown when the user is already running the installed PWA (or taps "Share"
   from the top nav after install) — invites them to share the calculator
   through WhatsApp / X / Telegram / Email / Facebook, system share, or
   plain copy-to-clipboard. */
export const InstalledShare = ({ open, onOpenChange, lang = "ar", theme = "dark" }) => {
  const dark = theme === "dark";
  const ar = lang === "ar";
  const [copied, setCopied] = useState(false);
  const baseUrl = typeof window !== "undefined" ? window.location.origin + "/" : "";
  const shareText = ar
    ? "جرّب حاسبة أسعار العمل الحر بالريال السعودي من برولانسر — اقتراحات ذكاء اصطناعي حسب السوق اليومي."
    : "Try the Saudi freelance pricing calculator by Prolancer — AI suggestions on top of reference Saudi market rates.";

  // Per-channel UTM-tagged URL so GA / Plausible can attribute installs & visits.
  const utmUrl = (source) =>
    `${baseUrl}?utm_source=${source}&utm_medium=share&utm_campaign=pwa_share`;

  // Platform targets — each is a graceful URL redirect (works on web & inside PWA).
  const targets = [
    {
      id: "whatsapp",
      label: ar ? "واتساب" : "WhatsApp",
      bg: "#25D366",
      build: () => `https://wa.me/?text=${encodeURIComponent(shareText)}%20${encodeURIComponent(utmUrl("whatsapp"))}`,
      icon: <MessageCircle className="h-5 w-5" />,
    },
    {
      id: "telegram",
      label: ar ? "تيليجرام" : "Telegram",
      bg: "#229ED9",
      build: () => `https://t.me/share/url?url=${encodeURIComponent(utmUrl("telegram"))}&text=${encodeURIComponent(shareText)}`,
      icon: <Send className="h-5 w-5" />,
    },
    {
      id: "twitter",
      label: ar ? "إكس" : "X",
      bg: "#182138",
      build: () => `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(utmUrl("twitter"))}`,
      icon: <Twitter className="h-5 w-5" />,
    },
    {
      id: "facebook",
      label: ar ? "فيسبوك" : "Facebook",
      bg: "#1877F2",
      build: () => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(utmUrl("facebook"))}`,
      icon: <Facebook className="h-5 w-5" />,
    },
    {
      id: "email",
      label: ar ? "بريد" : "Email",
      bg: "#64748b",
      build: () => `mailto:?subject=${encodeURIComponent(ar ? "حاسبة برولانسر" : "Prolancer Calculator")}&body=${encodeURIComponent(shareText)}%20${encodeURIComponent(utmUrl("email"))}`,
      icon: <Mail className="h-5 w-5" />,
    },
    {
      id: "sms",
      label: ar ? "رسالة" : "SMS",
      bg: "#18c98f",
      build: () => `sms:?body=${encodeURIComponent(shareText)}%20${encodeURIComponent(utmUrl("sms"))}`,
      icon: <Smartphone className="h-5 w-5" />,
    },
  ];

  const handleSystemShare = async () => {
    const data = {
      title: ar ? "حاسبة برولانسر" : "Prolancer Calculator",
      text: shareText,
      url: utmUrl("system"),
    };
    if (navigator.share) {
      try { await navigator.share(data); return; } catch (_) { /* user cancelled */ }
    }
    handleCopy();
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(utmUrl("copy"));
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch (_) {}
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="pwa-installed-share"
        dir={ar ? "rtl" : "ltr"}
        className={`${dark ? "bg-[#182138] text-white border-[#18c98f]/40" : "bg-white text-[#182138] border-[#18c98f]/50"} max-w-md`}
      >
        <DialogHeader>
          <DialogTitle className={`flex items-center gap-2 ${ar ? "font-arabic" : "font-outfit"}`}>
            <Share2 className="h-5 w-5 text-[#18c98f]" />
            {ar ? "شارك حاسبة برولانسر" : "Share Prolancer Calculator"}
          </DialogTitle>
          <DialogDescription className={`${dark ? "text-white/70" : "text-[#2d2c35]/80"} ${ar ? "font-arabic" : "font-outfit"}`}>
            {ar
              ? "اختر التطبيق الذي تريد المشاركة من خلاله — الرسالة والرابط جاهزان."
              : "Pick where you want to share — the message and link are ready."}
          </DialogDescription>
        </DialogHeader>

        {/* Social share grid */}
        <div className="grid grid-cols-3 gap-2 mt-2" dir="ltr">
          {targets.map((t) => (
            <a
              key={t.id}
              href={t.build()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`share-${t.id}`}
              className="flex flex-col items-center justify-center gap-1 h-20 rounded-xl text-white font-extrabold text-xs transition-transform hover:scale-[1.03] active:scale-[0.97]"
              style={{ backgroundColor: t.bg, boxShadow: "0 6px 20px -8px rgba(0,0,0,0.4)" }}
            >
              {t.icon}
              <span className={ar ? "font-arabic" : "font-outfit"}>{t.label}</span>
            </a>
          ))}
        </div>

        {/* Link preview */}
        <div className={`mt-3 p-2.5 rounded-xl border flex items-center justify-between gap-2 ${dark ? "border-[#2e3a5c] bg-[#1f2a47]" : "border-[#e5e5ea] bg-[#f3f8ff]"}`}>
          <span dir="ltr" className={`text-xs font-mono truncate flex-1 min-w-0 ${dark ? "text-white/80" : "text-[#182138]/80"}`}>
            {baseUrl}
          </span>
          <button
            onClick={handleCopy}
            data-testid="share-copy-btn"
            className={`shrink-0 inline-flex items-center gap-1 px-2.5 h-8 rounded-full text-xs font-bold ${
              dark ? "bg-[#18c98f]/20 text-[#18c98f] hover:bg-[#18c98f] hover:text-[#182138]" : "bg-[#18c98f]/15 text-[#12a777] hover:bg-[#18c98f] hover:text-white"
            } ${ar ? "font-arabic" : "font-outfit"}`}
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? (ar ? "تم" : "Done") : (ar ? "نسخ" : "Copy")}
          </button>
        </div>

        {/* System share (uses native share sheet when available) */}
        <button
          onClick={handleSystemShare}
          data-testid="share-system-btn"
          className={`mt-2 inline-flex items-center justify-center gap-2 w-full h-11 rounded-full font-extrabold bg-[#18c98f] text-white hover:brightness-110 ${ar ? "font-arabic" : "font-outfit"}`}
        >
          <Share2 className="h-4 w-4" />
          {ar ? "خيارات المشاركة في جهازك" : "More share options"}
        </button>
      </DialogContent>
    </Dialog>
  );
};

/* Inline hero install button — glowing green pill. Always visible when the app
   is not installed; after installation it transforms into a share CTA. */
export const InstallPWA = ({ lang = "ar", theme = "dark" }) => {
  const dark = theme === "dark";
  const { canInstall, isIOS, isAndroid, standalone, install } = usePWAInstall();
  const [helpOpen, setHelpOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const handleClick = async () => {
    if (standalone) {
      setShareOpen(true);
      return;
    }
    if (canInstall) {
      const accepted = await install();
      if (!accepted) setHelpOpen(true);
    } else {
      setHelpOpen(true);
    }
  };

  const platform = isIOS ? "ios" : isAndroid ? "android" : "generic";
  const label = standalone
    ? (lang === "ar" ? "مشاركة" : "Share")
    : (lang === "ar" ? "ثبّت التطبيق" : "Install app");

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        data-testid="pwa-install-btn"
        className={`inline-flex items-center gap-2 h-9 sm:h-10 px-3.5 sm:px-4 rounded-full border transition-all font-extrabold text-xs sm:text-sm ${
          dark
            ? "bg-[#1f2a47] border-[#18c98f]/40 text-white hover:bg-[#18c98f] hover:text-[#182138]"
            : "bg-white border-[#18c98f]/50 text-[#182138] hover:bg-[#18c98f] hover:text-white"
        } ${lang === "ar" ? "font-arabic" : "font-outfit"}`}
        style={{ boxShadow: "0 0 18px -6px rgba(24,201,143,0.45)" }}
      >
        {standalone
          ? <Share2 className="h-4 w-4 text-[#18c98f]" />
          : <Download className="h-4 w-4 text-[#18c98f]" />}
        <span>{label}</span>
      </button>
      <InstallHelp open={helpOpen} onOpenChange={setHelpOpen} lang={lang} theme={theme} platform={platform} />
      <InstalledShare open={shareOpen} onOpenChange={setShareOpen} lang={lang} theme={theme} />
    </>
  );
};

export default InstallPWA;
